import { CompanyRecommendationsList_organization$key } from '#app/__generated__/relay/CompanyRecommendationsList_organization.graphql.js';
import {
  CompanyRecommendationsListPagination_organization$data,
  CompanyRecommendationsListPagination_organization$key,
} from '#app/__generated__/relay/CompanyRecommendationsListPagination_organization.graphql.js';
import CompanyRecommendationsListQueryNode, {
  type CompanyRecommendationsListQuery as CompanyRecommendationsListQueryType,
} from '#app/__generated__/relay/CompanyRecommendationsListQuery.graphql.js';
import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { AverageCompanyReviewScoreBreakdown } from '#app/components/companyProfilesV2/components/CompanyRecommendations/AverageCompanyReviewScoreBreakdown.js';
import { CompanyReviewCard } from '#app/components/companyProfilesV2/components/CompanyRecommendations/CompanyReview.js';
import { NewWorkOpportunitySkeleton } from '#app/components/SkeletonLoaders/NewWorkOpportunitySkeleton.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { usePaginatedInfiniteLoad } from '#app/hooks/usePaginatedInfiniteLoad.js';
import { OverviewRecommendationsSectionEmptyState } from '../EmptyStates/OverviewRecommendationsSectionEmptyState.js';
import { CollapsibleOtherReviews } from './CollapsibleOtherReviews.js';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
} from '@contra/relay';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const List = styled.div<{ $isEmpty?: boolean }>`
  ${({ $isEmpty = false }) => css`
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.colorsV2.gray30};

    ${!$isEmpty &&
    css`
      padding: 24px 24px 0 24px;
    `}
  `}
`;

type ReviewNode = NonNullable<
  CompanyRecommendationsListPagination_organization$data['userReviews']['edges']
>[number]['node'];

export const CompanyRecommendationsList = (props: {
  readonly organizationRef: CompanyRecommendationsList_organization$key;
}) => {
  const [isConsolidatedReviewsOpen, setIsConsolidatedReviewsOpen] =
    useState<boolean>(false);
  const { track } = useAnalytics();
  const data = useFragment(
    graphql`
      fragment CompanyRecommendationsList_organization on Organization {
        id
        ...AverageCompanyReviewScoreBreakdown_organization
        ...OverviewRecommendationsSectionEmptyState_organization
      }
    `,
    props.organizationRef,
  );

  const query = useLazyLoadQuery<CompanyRecommendationsListQueryType>(
    CompanyRecommendationsListQueryNode,
    {
      first: 10,
      id: data.id,
    },
    { fetchPolicy: 'store-and-network' },
  );

  const {
    data: userReviewData,
    hasNext,
    isLoadingNext,
    loadNext,
  } = usePaginationFragment<
    CompanyRecommendationsListQueryType,
    CompanyRecommendationsListPagination_organization$key
  >(
    graphql`
      fragment CompanyRecommendationsListPagination_organization on Organization
      @refetchable(queryName: "CompanyRecommendationsListQuery") {
        visitorIsMember
        userReviews(first: $first, after: $after)
          @connection(key: "CompanyRecommendationsList_userReviews") {
          count
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          edges {
            node {
              id
              createdAt
              reviewText
              author {
                id
                firstName
                avatarImage {
                  url
                  uid
                }
              }
              ...CompanyReviewCard_userReview
            }
          }
        }
      }
    `,
    query.node,
  );

  const loadMore = useCallback(() => {
    loadNext(10);
  }, [loadNext]);

  const loadMoreElementRef = usePaginatedInfiniteLoad<HTMLDivElement>(
    loadMore,
    hasNext,
    isLoadingNext,
    { rootMargin: '200px', threshold: 0 },
  );

  const onClickSeeMoreReviews = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'company_profile_see_consolidated_reviews',
    });

    setIsConsolidatedReviewsOpen(true);
  }, [setIsConsolidatedReviewsOpen, track]);

  const onClickHideMoreReviews = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'company_profile_hide_consolidated_reviews',
    });

    setIsConsolidatedReviewsOpen(false);
  }, [setIsConsolidatedReviewsOpen, track]);

  if (userReviewData?.userReviews.count === 0) {
    return (
      <List $isEmpty>
        <OverviewRecommendationsSectionEmptyState organizationRef={data} />
      </List>
    );
  }

  const dataReviews = userReviewData?.userReviews.edges ?? [];
  const [reviews, reviewsWithoutText] = dataReviews.reduce<
    [ReviewNode[], ReviewNode[]]
  >(
    (accumulator, edge) => {
      if (edge.node.reviewText) {
        accumulator[0].push(edge.node);
      } else {
        accumulator[1].push(edge.node);
      }

      return accumulator;
    },
    [[], []],
  );

  const uniqueAuthors = reviewsWithoutText.reduce(
    (accumulator, currentNode) => {
      const currentUser = currentNode.author.id;
      if (!accumulator.has(currentUser)) {
        accumulator.set(currentUser, currentNode);
      }

      return accumulator;
    },
    new Map<string, ReviewNode>(),
  );
  const uniqueAuthorsArray = Array.from(uniqueAuthors.values());

  return (
    <>
      <List>
        <AverageCompanyReviewScoreBreakdown organizationRef={data} />
        {reviews.map((node) => {
          return (
            <CompanyReviewCard
              hideRecipient={!userReviewData?.visitorIsMember}
              key={node.id}
              reference={node}
            />
          );
        })}
        {reviewsWithoutText.length ? (
          <CollapsibleOtherReviews
            avatarGroup={
              <AvatarGroup
                data={uniqueAuthorsArray.map((node) => ({
                  avatarImage: node.author.avatarImage,
                  fallbackName: node.author.firstName ?? undefined,
                  key: node.author.id,
                }))}
                maxCount={5}
                size="xs"
              />
            }
            isOpen={isConsolidatedReviewsOpen}
            label={`Recommended ${reviewsWithoutText.length} other times`}
            onClickClose={onClickHideMoreReviews}
            onClickOpen={onClickSeeMoreReviews}
          >
            {reviewsWithoutText.map((node) => {
              return (
                <CompanyReviewCard
                  hideRecipient={!userReviewData?.visitorIsMember}
                  key={node.id}
                  reference={node}
                />
              );
            })}
          </CollapsibleOtherReviews>
        ) : null}
      </List>
      <NewWorkOpportunitySkeleton
        isLoading={isLoadingNext}
        ref={loadMoreElementRef}
      />
    </>
  );
};
